// Terminal catalog data — single source of truth.
//
// Product images live at /app/frontend/public/terminals/{slug}.png
// (real photos uploaded by client).

export const BRANDS = [
  {
    id: "valor",
    name: "Valor",
    description:
      "Valor offers flexible payment technology for businesses that need reliable in-store, mobile, and digital payment options. From countertop and wireless devices to mobile app tools and online portal access, Valor solutions are built to help businesses accept payments with confidence.",
  },
  {
    id: "dejavoo",
    name: "Dejavoo",
    description:
      "Dejavoo terminals are built for businesses that need dependable payment acceptance with modern touchscreen options, wireless capability, chip, swipe, tap, and EBT support on select models. These terminals are a strong fit for retail stores, restaurants, delis, convenience stores, and service businesses.",
  },
  {
    id: "pax",
    name: "PAX",
    description:
      "PAX devices offer modern smart terminal technology with clean touchscreen interfaces, secure payment acceptance, and flexible hardware options for countertop, pin pad, and mobile environments.",
  },
];

export const CATEGORY_LABEL = {
  countertop: "Countertop",
  smart: "Smart Terminal",
  pinpad: "Pin Pad",
  mobile: "Mobile",
  software: "Software",
};

export const TERMINALS = [
  // ─── VALOR ────────────────────────────────────────────────
  {
    slug: "valor-vl100",
    name: "Valor VL100",
    brand: "valor",
    brandName: "Valor",
    category: "countertop",
    tagline: "Countertop terminal for fast, secure checkout.",
    summary:
      "The Valor VL100 is a countertop payment terminal designed for fast, secure in-store checkout.",
    features: [
      "EMV, contactless, swipe",
      "Built-in printer",
      "Ethernet and Wi-Fi",
      "Encrypted PIN entry",
    ],
    bestFor:
      "Retail stores, restaurants, delis, convenience stores, and service businesses.",
    description:
      "The Valor VL100 is a countertop payment terminal designed for fast, secure in-store checkout. It supports modern payment acceptance and is a strong fit for retail stores, restaurants, delis, convenience stores, and service businesses.",
    moreInfo:
      "Works well with cash discount and dual pricing setups.",
    specs: [
      ["Form factor", "Countertop"],
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated thermal"],
    ],
  },
  {
    slug: "valor-vl110",
    name: "Valor VL110",
    brand: "valor",
    brandName: "Valor",
    category: "mobile",
    tagline: "Handheld terminal built for mobility.",
    summary:
      "The Valor VL110 is a handheld payment terminal built for businesses that need flexible checkout options.",
    features: [
      "Handheld portable design",
      "EMV, contactless, swipe",
      "Wireless connectivity",
      "Customer-facing payments",
    ],
    bestFor:
      "Pay-at-table, line-busting, and mobile checkout.",
    description:
      "The Valor VL110 is a handheld payment terminal built for businesses that need flexible checkout options. It is designed for mobility, customer-facing payments, and fast card acceptance.",
    moreInfo:
      "Strong fit for full-service restaurants and service businesses on the move.",
    specs: [
      ["Form factor", "Handheld"],
      ["Connectivity", "Wireless"],
      ["Acceptance", "EMV, contactless, swipe"],
    ],
  },
  {
    slug: "valor-vl100-pro",
    name: "Valor VL100 Pro",
    brand: "valor",
    brandName: "Valor",
    category: "countertop",
    tagline: "Upgraded countertop terminal with a modern checkout experience.",
    summary:
      "The Valor VL100 Pro is an upgraded countertop payment terminal designed for businesses that want a modern checkout experience.",
    features: [
      "Upgraded hardware and performance",
      "EMV, contactless, swipe",
      "Built-in printer",
      "Ethernet and Wi-Fi",
    ],
    bestFor:
      "Businesses that want a modern checkout experience with professional hardware.",
    description:
      "The Valor VL100 Pro is an upgraded countertop payment terminal designed for businesses that want a modern checkout experience with professional hardware and secure payment acceptance.",
    moreInfo:
      "A strong upgrade path from the standard VL100.",
    specs: [
      ["Form factor", "Countertop"],
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "valor-vl500",
    name: "Valor VL500",
    brand: "valor",
    brandName: "Valor",
    category: "smart",
    tagline: "Android smart terminal with touchscreen checkout.",
    summary:
      "The Valor VL500 is an Android smart terminal designed for businesses that want a modern touchscreen payment experience.",
    features: [
      "Android operating system",
      "Color touchscreen",
      "EMV, contactless, swipe",
      "Built-in printer",
    ],
    bestFor:
      "Countertop checkout, customer-facing payments, and flexible payment acceptance.",
    description:
      "The Valor VL500 is an Android smart terminal designed for businesses that want a modern touchscreen payment experience. It is a strong option for countertop checkout, customer-facing payments, and flexible payment acceptance.",
    moreInfo:
      "Pairs well with cash discount and dual pricing programs.",
    specs: [
      ["OS", "Android"],
      ["Display", "Color touchscreen"],
      ["Connectivity", "Wi-Fi, Ethernet"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "valor-vp100",
    name: "Valor VP100",
    brand: "valor",
    brandName: "Valor",
    category: "countertop",
    tagline: "Reliable countertop terminal for everyday business use.",
    summary:
      "The Valor VP100 is a reliable countertop payment terminal built for everyday business use.",
    features: [
      "EMV, contactless, swipe",
      "Built-in printer",
      "Ethernet and Wi-Fi",
      "Quick checkout workflow",
    ],
    bestFor:
      "Small retail, quick-service, and service businesses.",
    description:
      "The Valor VP100 is a reliable countertop payment terminal built for everyday business use. It is designed to support secure card acceptance, quick checkout, and simple payment processing.",
    moreInfo:
      "Works well with cash discount and dual pricing programs.",
    specs: [
      ["Form factor", "Countertop"],
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "valor-vl300-pinpad",
    name: "Valor VL300 Pin Pad",
    brand: "valor",
    brandName: "Valor",
    category: "pinpad",
    tagline: "Compact PIN pad for secure customer-facing entry.",
    summary:
      "The Valor VL300 is a compact pin pad designed for secure customer-facing payment entry.",
    features: [
      "Encrypted PIN entry",
      "EMV, contactless, swipe",
      "Compact countertop footprint",
      "Reliable customer-facing design",
    ],
    bestFor:
      "Businesses that want a simple, professional PIN pad solution at checkout.",
    description:
      "The Valor VL300 is a compact pin pad designed for secure customer-facing payment entry. It works well for businesses that want a simple, professional PIN pad solution at checkout.",
    moreInfo:
      "Pairs with compatible POS systems and Valor terminals.",
    specs: [
      ["Form factor", "PIN pad"],
      ["Acceptance", "EMV, contactless, swipe"],
      ["Security", "Encrypted PIN entry"],
    ],
  },
  {
    slug: "valor-vp800",
    name: "Valor VP800",
    brand: "valor",
    brandName: "Valor",
    category: "smart",
    tagline: "Modern smart terminal with a large touchscreen.",
    summary:
      "The Valor VP800 is a modern smart payment terminal with a large touchscreen and sleek design.",
    features: [
      "Large touchscreen display",
      "Android operating system",
      "EMV, contactless, swipe, QR",
      "Built-in printer",
    ],
    bestFor:
      "Businesses that want a premium checkout experience with secure payment acceptance.",
    description:
      "The Valor VP800 is a modern smart payment terminal with a large touchscreen and sleek design. It is built for businesses that want a premium checkout experience with secure payment acceptance.",
    moreInfo:
      "Sleek premium look on the counter — great for retail and hospitality.",
    specs: [
      ["OS", "Android"],
      ["Display", "Large touchscreen"],
      ["Connectivity", "Wi-Fi, Ethernet, optional 4G"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "valor-vp550",
    name: "Valor VP550",
    brand: "valor",
    brandName: "Valor",
    category: "smart",
    tagline: "Premium Android smart terminal for modern checkout.",
    summary:
      "Valor's flagship smart terminal — a refined, customer-facing device built for businesses that want a clean, modern checkout experience.",
    features: [
      "Android operating system",
      "Large color touchscreen",
      "EMV, contactless, swipe, QR",
      "Built-in printer",
      "Wi-Fi, Ethernet, optional 4G",
    ],
    bestFor:
      "Restaurants, retail stores, salons, and service businesses that want a customer-facing smart terminal experience.",
    description:
      "The Valor VP550 is the flagship smart terminal in the Valor lineup. It pairs a clean Android interface with full payment acceptance, making it ideal for modern counters that need richer features like tipping, dual pricing, and integrated loyalty.",
    moreInfo:
      "Strong pairing for cash discount and dual pricing programs.",
    specs: [
      ["OS", "Android"],
      ["Display", "Large color touchscreen"],
      ["Connectivity", "Wi-Fi, Ethernet, optional 4G"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "valor-vp300",
    name: "Valor VP300",
    brand: "valor",
    brandName: "Valor",
    category: "countertop",
    tagline: "Compact countertop terminal — clean and reliable.",
    summary:
      "A dependable countertop terminal designed for businesses that want straightforward, secure payment acceptance.",
    features: [
      "EMV, contactless, swipe",
      "Built-in printer",
      "Ethernet and Wi-Fi",
      "Encrypted PIN entry",
    ],
    bestFor:
      "Small retail, convenience, quick-service food, and service businesses.",
    description:
      "The Valor VP300 is a clean, low-profile countertop terminal — fast, secure, and easy to train staff on. A common everyday workhorse at the counter.",
    moreInfo: "Works well with cash discount and dual pricing programs.",
    specs: [
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated thermal"],
    ],
  },
  {
    slug: "valor-rckt-emv-dongle",
    name: "Valor RCKT EMV Mobile Dongle",
    brand: "valor",
    brandName: "Valor",
    category: "mobile",
    tagline: "Mobile dongle that turns any phone into a terminal.",
    summary:
      "A pocket-sized EMV-compliant card reader that pairs with a smartphone or tablet to accept payments anywhere.",
    features: [
      "EMV chip + contactless",
      "Bluetooth pairing",
      "Works with iOS and Android",
      "Compact and portable",
    ],
    bestFor:
      "Mobile sellers, food trucks, contractors, and businesses on the go.",
    description:
      "The Valor RCKT EMV Mobile Dongle is a portable card reader that pairs with the Valor mobile app to turn any smartphone or tablet into a full payment terminal — anywhere with cell signal.",
    moreInfo:
      "Great for trade shows, pop-ups, delivery, field service, and mobile salons.",
    specs: [
      ["Connection", "Bluetooth"],
      ["Compatibility", "iOS, Android"],
      ["Acceptance", "EMV, contactless"],
    ],
  },
  {
    slug: "valor-mobile-app",
    name: "Valor Mobile App",
    brand: "valor",
    brandName: "Valor",
    category: "mobile",
    tagline: "Accept and manage payments from a mobile device.",
    summary:
      "Valor's mobile app turns a phone or tablet into a full payments hub — sales, refunds, reporting, and customer management on the go.",
    features: [
      "Accept card and digital payments",
      "Sales and deposit reporting",
      "Refunds and voids",
      "Customer and transaction history",
    ],
    bestFor:
      "Mobile businesses, field service, contractors, and owners who manage from their phone.",
    description:
      "The Valor Mobile App is the companion to the RCKT dongle and Valor terminal lineup. Run your business from a phone — take payments, send receipts, issue refunds, and review activity.",
    moreInfo:
      "Pairs natively with the RCKT EMV dongle and works alongside Valor countertop devices.",
    specs: [
      ["Platforms", "iOS, Android"],
      ["Features", "Payments, reporting, refunds"],
      ["Connectivity", "Wi-Fi or cellular"],
    ],
  },
  {
    slug: "valor-online-portal",
    name: "Valor Online Portal",
    brand: "valor",
    brandName: "Valor",
    category: "software",
    tagline: "Web dashboard for your Valor account.",
    summary:
      "A clean, web-based portal for reviewing sales, deposits, transactions, and managing the Valor account from any browser.",
    features: [
      "Sales and deposit overview",
      "Transaction search and history",
      "Refunds and voids from the browser",
      "Statement and reporting access",
    ],
    bestFor:
      "Business owners and managers who want desktop-level visibility.",
    description:
      "The Valor Online Portal gives business owners a clean, web-based view of their payment activity — perfect for back-office management, accountant access, and multi-location operators.",
    moreInfo:
      "Pairs with every Valor terminal and the Valor Mobile App.",
    specs: [
      ["Type", "Web portal"],
      ["Access", "Any modern browser"],
      ["Features", "Reporting, refunds, transaction search"],
    ],
  },
  // ─── DEJAVOO ──────────────────────────────────────────────
  {
    slug: "dejavoo-p5",
    name: "Dejavoo P5",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "pinpad",
    tagline: "Premium PIN pad with larger touchscreen display.",
    summary:
      "A modern PIN pad with a touchscreen-style customer display for richer prompts, tipping, and digital signatures.",
    features: [
      "Large customer display",
      "Tip prompts and signature capture",
      "EMV, contactless, swipe",
      "Encrypted PIN entry",
    ],
    bestFor:
      "Restaurants and service businesses that lean on tips.",
    description:
      "The Dejavoo P5 brings a richer customer experience to the counter with a bigger display that supports tip prompts, signatures, and digital receipts.",
    moreInfo: "A common upgrade for restaurants and service businesses.",
    specs: [
      ["Display", "Large color touchscreen"],
      ["Connection", "USB"],
      ["Features", "Tip prompts, signature capture"],
    ],
  },
  {
    slug: "dejavoo-qd5",
    name: "Dejavoo QD5",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "smart",
    tagline: "Flagship Android smart terminal in the QD line.",
    summary:
      "Dejavoo's premium Android smart terminal — a large touchscreen device built for modern counters.",
    features: [
      "Android touchscreen interface",
      "EMV, contactless, swipe, QR",
      "Wi-Fi, Ethernet, optional 4G",
      "Built-in printer",
    ],
    bestFor:
      "Retailers, restaurants, and service businesses that want a premium customer-facing experience.",
    description:
      "The Dejavoo QD5 is the top of the QD line — large screen, fast processor, full payment acceptance, and a clean Android experience.",
    moreInfo: "Pairs well with cash discount and dual pricing setups.",
    specs: [
      ["OS", "Android"],
      ["Display", "Large color touchscreen"],
      ["Connectivity", "Wi-Fi, Ethernet, optional 4G"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "dejavoo-z9",
    name: "Dejavoo Z9",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "countertop",
    tagline: "Premium countertop terminal in the Z lineup.",
    summary:
      "A reliable countertop terminal with full payment acceptance and a clean form factor.",
    features: [
      "EMV, contactless, swipe",
      "Built-in printer",
      "Ethernet and Wi-Fi",
      "PIN debit support",
    ],
    bestFor:
      "Retailers, convenience stores, and quick-service food businesses.",
    description:
      "The Dejavoo Z9 is a refined countertop terminal — solid build, fast performance, and clear customer flow.",
    moreInfo:
      "Often deployed in cash discount and dual pricing environments.",
    specs: [
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated thermal"],
    ],
  },
  {
    slug: "dejavoo-p8",
    name: "Dejavoo P8",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "pinpad",
    tagline: "High-end Android customer-facing PIN device.",
    summary:
      "A premium customer-facing payment device with an Android touchscreen, modern UI, and full payment acceptance.",
    features: [
      "Android touchscreen UI",
      "EMV, contactless, swipe, QR",
      "Tip prompts, loyalty support",
      "Encrypted PIN entry",
    ],
    bestFor:
      "Counters that want a sharp, modern customer checkout experience.",
    description:
      "The Dejavoo P8 sits at the top of the PIN-pad lineup with an Android-powered touchscreen experience — closer to a smart terminal than a traditional PIN pad.",
    moreInfo:
      "A great upgrade for businesses on integrated POS stacks.",
    specs: [
      ["OS", "Android"],
      ["Display", "Large touchscreen"],
      ["Acceptance", "EMV, contactless, swipe, QR"],
    ],
  },
  {
    slug: "dejavoo-p3",
    name: "Dejavoo P3",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "pinpad",
    tagline: "Mid-size PIN pad with clear customer display.",
    summary:
      "An ergonomic PIN pad with a customer-facing display, ideal for retail counters running modern POS software.",
    features: [
      "EMV, contactless, swipe",
      "Customer-facing display",
      "USB or serial connection",
      "Encrypted PIN entry",
    ],
    bestFor:
      "Retailers moving to integrated POS payments.",
    description:
      "The Dejavoo P3 balances size and presence at the counter — large enough to feel solid in customer use, small enough to fit cleanly next to a register.",
    moreInfo:
      "A reliable everyday PIN pad for integrated POS environments.",
    specs: [
      ["Connection", "USB / Serial"],
      ["Display", "Customer-facing"],
      ["Acceptance", "EMV, contactless, swipe"],
    ],
  },
  {
    slug: "dejavoo-qd2",
    name: "Dejavoo QD2",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "smart",
    tagline: "Mid-range Android smart terminal.",
    summary:
      "A balanced Android smart terminal — modern features, clean design, sensible footprint.",
    features: [
      "Android touchscreen",
      "EMV, contactless, swipe",
      "Wi-Fi, optional 4G",
      "Built-in printer",
    ],
    bestFor:
      "Small to mid-size retailers and service businesses.",
    description:
      "The Dejavoo QD2 brings most of the QD line's strengths to a slightly smaller footprint — perfect for counters that don't need the flagship size.",
    moreInfo:
      "Solid pairing with cash discount or dual pricing programs.",
    specs: [
      ["OS", "Android"],
      ["Display", "Color touchscreen"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "dejavoo-qd1",
    name: "Dejavoo QD1",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "smart",
    tagline: "Entry-level Android smart terminal in the QD line.",
    summary:
      "A compact smart terminal designed for businesses that want Android features without a large footprint.",
    features: [
      "Android touchscreen",
      "EMV, contactless, swipe",
      "Wi-Fi connectivity",
      "Built-in printer",
    ],
    bestFor:
      "Smaller retail and service counters.",
    description:
      "The Dejavoo QD1 is the entry-level option in the QD smart terminal family — clean, modern, and easy to deploy.",
    moreInfo:
      "Strong upgrade from older traditional terminals.",
    specs: [
      ["OS", "Android"],
      ["Display", "Color touchscreen"],
      ["Connectivity", "Wi-Fi"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "dejavoo-z11",
    name: "Dejavoo Z11",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "countertop",
    tagline: "Compact countertop terminal with built-in printer.",
    summary:
      "A reliable, small-footprint countertop terminal for businesses that need fast, secure payment acceptance.",
    features: [
      "EMV chip, contactless, magstripe",
      "Built-in thermal printer",
      "Ethernet and Wi-Fi",
      "PIN debit support",
    ],
    bestFor:
      "Small retail, quick service, convenience, and service businesses.",
    description:
      "The Dejavoo Z11 is a compact, dependable countertop terminal designed for busy counters that need every transaction to move quickly.",
    moreInfo:
      "Pairs easily with cash discount and dual pricing programs.",
    specs: [
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated thermal"],
    ],
  },
  {
    slug: "dejavoo-z8",
    name: "Dejavoo Z8",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "countertop",
    tagline: "Traditional countertop terminal — proven and reliable.",
    summary:
      "A trusted countertop terminal designed for everyday business use.",
    features: [
      "EMV, contactless, swipe",
      "Built-in printer",
      "Ethernet and Wi-Fi",
      "Easy staff training",
    ],
    bestFor:
      "Retail, convenience, deli, and service businesses.",
    description:
      "The Dejavoo Z8 is a workhorse countertop terminal known for its reliability and clean form factor.",
    moreInfo: "A staple in cash discount and dual pricing deployments.",
    specs: [
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated thermal"],
    ],
  },
  {
    slug: "dejavoo-z6",
    name: "Dejavoo Z6",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "countertop",
    tagline: "Compact entry-level countertop terminal.",
    summary:
      "A small-footprint countertop terminal that covers the essentials at a clean price point.",
    features: [
      "EMV, contactless, swipe",
      "Compact build",
      "Ethernet and Wi-Fi",
      "Encrypted PIN entry",
    ],
    bestFor:
      "Smaller counters, kiosks, and businesses with limited counter space.",
    description:
      "The Dejavoo Z6 is the smallest in the Z line — perfect for businesses that need full acceptance in a tight footprint.",
    moreInfo: "Solid entry-level terminal for cost-conscious deployments.",
    specs: [
      ["Acceptance", "EMV, contactless, swipe"],
      ["Connectivity", "Ethernet, Wi-Fi"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "dejavoo-p1",
    name: "Dejavoo P1",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "pinpad",
    tagline: "Compact encrypted PIN pad for countertop pairing.",
    summary:
      "A small, secure PIN pad that connects to compatible terminals and POS systems.",
    features: [
      "EMV chip and contactless",
      "Encrypted PIN entry",
      "USB-connected",
      "Compact footprint",
    ],
    bestFor:
      "Businesses adding PIN debit, contactless, or chip acceptance to existing setups.",
    description:
      "The Dejavoo P1 is a low-profile encrypted PIN pad designed to extend full payment acceptance to compatible terminals.",
    moreInfo: "Common pairing for retail counters.",
    specs: [
      ["Connection", "USB"],
      ["Acceptance", "EMV, contactless, PIN debit"],
      ["Security", "Encrypted PIN entry"],
    ],
  },
  {
    slug: "dejavoo-qd4",
    name: "Dejavoo QD4",
    brand: "dejavoo",
    brandName: "Dejavoo",
    category: "smart",
    tagline: "Sleek Android smart terminal with touchscreen.",
    summary:
      "A modern Android-powered smart terminal designed for clean countertop deployment with rich features.",
    features: [
      "Android touchscreen interface",
      "EMV, contactless, swipe, QR",
      "Wi-Fi and 4G connectivity",
      "Built-in printer",
    ],
    bestFor:
      "Counters that want a step up from a traditional terminal without a full POS.",
    description:
      "The Dejavoo QD4 brings a modern smart-terminal experience to the counter — bright touchscreen, fast processor, and full payment acceptance.",
    moreInfo: "Great upgrade path from older traditional terminals.",
    specs: [
      ["OS", "Android"],
      ["Display", "Color touchscreen"],
      ["Connectivity", "Wi-Fi, optional 4G"],
      ["Printer", "Integrated"],
    ],
  },

  // ─── PAX ──────────────────────────────────────────────────
  {
    slug: "pax-aries8",
    name: "PAX ARIES8",
    brand: "pax",
    brandName: "PAX",
    category: "smart",
    tagline: "Modern Android smart terminal with bright touchscreen.",
    summary:
      "PAX's ARIES8 is a sharp Android smart terminal designed for businesses that want a modern, polished checkout experience.",
    features: [
      "Android operating system",
      "Bright color touchscreen",
      "EMV, contactless, swipe",
      "Built-in printer",
      "Wi-Fi, Ethernet, optional 4G",
    ],
    bestFor:
      "Retailers, restaurants, and service businesses upgrading to a smart terminal.",
    description:
      "The PAX ARIES8 is one of PAX's newer smart terminals — clean Android UI, fast hardware, and a premium customer-facing presence at the counter.",
    moreInfo:
      "Strong choice for cash discount and dual pricing setups.",
    specs: [
      ["OS", "Android"],
      ["Display", "Color touchscreen"],
      ["Connectivity", "Wi-Fi, Ethernet, optional 4G"],
      ["Printer", "Integrated"],
    ],
  },
  {
    slug: "pax-a35",
    name: "PAX A35",
    brand: "pax",
    brandName: "PAX",
    category: "pinpad",
    tagline: "Compact Android customer-facing PIN device.",
    summary:
      "A modern Android-based PIN pad with a touchscreen for tipping, signatures, and digital prompts.",
    features: [
      "Android touchscreen",
      "EMV, contactless, swipe",
      "Tip prompts and signature capture",
      "Encrypted PIN entry",
    ],
    bestFor:
      "Retailers and restaurants integrating with modern POS software.",
    description:
      "The PAX A35 pairs with compatible POS systems to give customers a modern, intuitive payment experience right at the counter.",
    moreInfo:
      "A good fit for newer POS software stacks.",
    specs: [
      ["OS", "Android"],
      ["Display", "Touchscreen"],
      ["Acceptance", "EMV, contactless, swipe"],
    ],
  },
  {
    slug: "pax-a80",
    name: "PAX A80",
    brand: "pax",
    brandName: "PAX",
    category: "smart",
    tagline: "Android smart desktop with full-size touchscreen.",
    summary:
      "A powerful Android-based smart terminal with a vertical touchscreen, integrated printer, and full payment acceptance.",
    features: [
      "Android operating system",
      "EMV, contactless, swipe",
      "Integrated printer",
      "Wi-Fi, Ethernet, optional 4G",
    ],
    bestFor:
      "Retailers, salons, and service businesses that want modern smart-terminal features.",
    description:
      "The PAX A80 brings the power of an Android smart terminal to the counter in a compact, desk-friendly form factor.",
    moreInfo:
      "Compact but full-featured — fits any register footprint.",
    specs: [
      ["OS", "Android"],
      ["Display", "Color touchscreen"],
      ["Connectivity", "Wi-Fi, Ethernet, optional 4G"],
      ["Printer", "Integrated"],
    ],
  },
];
